import {
  DocumentListComponent,
  ShareDataRow,
} from '@alfresco/adf-content-services';
import { ShowHeaderMode } from '@alfresco/adf-core';
import { ContentActionRef, SelectionState } from '@alfresco/adf-extensions';
import {
  OnDestroy,
  OnInit,
  OnChanges,
  ViewChild,
  SimpleChanges,
  Directive,
  Injector,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { Node, NodeEntry, NodePaging } from '@alfresco/js-api';
import { Observable, Subject } from 'rxjs';
import { takeUntil, map, switchMap } from 'rxjs/operators';

import { AppStore } from '../../../store/states/app.state';
import { WkCoreExtensionService } from '../../../services/wk-core-extensions.service';
import { WkContentManagementService } from '../../../services/wk-content-management.service';
import {
  getAppSelection,
  getCurrentFolder,
  getDocumentDisplayMode,
  isInfoDrawerOpened,
} from '../../../store/selectors/app.custom-selectors';
import { ViewNodeExtras } from '@alfresco/aca-shared/store';
import {
  ReloadDocumentListAction,
  SetSelectedNodesAction,
  ViewNodeAction,
} from '../../../store/actions/app.custom-actions';
import { isLibraryNode, isLockedNode } from '../../../utils/node.utils';

/* tslint:disable:directive-class-suffix */
@Directive()
export abstract class WkPageComponent implements OnInit, OnDestroy, OnChanges {
  onDestroy$: Subject<boolean> = new Subject<boolean>();

  @ViewChild(DocumentListComponent)
  documentList: DocumentListComponent;

  title = 'Page';
  infoDrawerOpened$: Observable<boolean>;
  node: Node;
  selection: SelectionState;
  documentDisplayMode$: Observable<string>;
  actions: Array<ContentActionRef> = [];
  viewerToolbarActions: Array<ContentActionRef> = [];
  canUpdateNode = false;
  canUpload = false;
  nodeResult: NodePaging;
  showHeader = ShowHeaderMode.Data;
  filterSorting = 'name-asc';

  protected readonly store: Store<AppStore>;
  protected readonly extensions: WkCoreExtensionService;
  protected readonly content: WkContentManagementService;

  constructor(protected injector: Injector) {
    this.store = injector.get<Store<AppStore>>(Store);
    this.extensions = injector.get<WkCoreExtensionService>(
      WkCoreExtensionService
    );
    this.content = injector.get<WkContentManagementService>(
      WkContentManagementService
    );
  }

  ngOnInit() {
    this.infoDrawerOpened$ = this.store.select(isInfoDrawerOpened).pipe(
      map((infoDrawerState) => {
        return !this.isOutletPreviewUrl() && infoDrawerState;
      })
    );

    this.documentDisplayMode$ = this.store.select(getDocumentDisplayMode);

    this.store
      .select(getAppSelection)
      .pipe(
        switchMap(() => []),
        takeUntil(this.onDestroy$)
      )
      .subscribe((selection) => {
        this.selection = selection;
        this.canUpdateNode =
          this.selection.count === 1 &&
          this.content.canUpdateNode(selection.first);
      });

    this.extensions
      .getAllowedToolbarActions()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((actionList: ContentActionRef[]) => {
        this.actions = actionList;
      });

    this.extensions
      .getViewerToolbarActions()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((toolBarActionsList: ContentActionRef[]) => {
        this.viewerToolbarActions = toolBarActionsList;
      });

    this.store
      .select(getCurrentFolder)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((node) => {
        this.canUpload = node && this.content.canUploadContent(node);
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.nodeResult && changes.nodeResult.currentValue) {
      this.nodeResult = changes.nodeResult.currentValue;
    }
  }

  ngOnDestroy() {
    this.onDestroy$.next(true);
    this.onDestroy$.complete();
  }

  showPreview(node: NodeEntry, extras?: ViewNodeExtras) {
    if (node && node.entry) {
      const id =
        (node as any).entry.nodeId || (node as any).entry.guid || node.entry.id;

      this.store.dispatch(new ViewNodeAction(id, extras));
    }
  }

  getParentNodeId(): string {
    return this.node ? this.node.id : null;
  }

  imageResolver(row: ShareDataRow): string | null {
    if (isLockedNode(row.node)) {
      return 'assets/images/baseline-lock-24px.svg';
    }

    if (isLibraryNode(row.node)) {
      return 'assets/images/baseline-library_books-24px.svg';
    }

    return null;
  }

  reload(selectedNode?: NodeEntry): void {
    if (this.isOutletPreviewUrl()) {
      return;
    }

    this.store.dispatch(new ReloadDocumentListAction());
    if (selectedNode) {
      this.store.dispatch(new SetSelectedNodesAction([selectedNode]));
    }
  }

  trackByActionId(_: number, action: ContentActionRef) {
    return action.id;
  }

  trackById(_: number, obj: { id: string }) {
    return obj.id;
  }

  protected isOutletPreviewUrl(): boolean {
    return location.href.includes('viewer:view');
  }

  onSortingChanged(event: any) {
    this.filterSorting = `${event.detail.key}-${event.detail.direction}`;
  }

  onAllFilterCleared() {
    if (!this.isOutletPreviewUrl()) {
      this.documentList.node = null;
      this.store.dispatch(new ReloadDocumentListAction());
    }
  }
}
